import React, { useRef, useState, useEffect, useLayoutEffect, useCallback } from 'react';
import { useSvgStore } from '../../store/svgStore';
import { SvgNodeElement } from '../../types/svgTypes';
import { injectAnimationToSvg } from '../../utils/svgAnimationUtils';
import { prepareSvgForEditMode, rebuildSvgContent } from '../../utils/svgParser';

interface SelectionBox {
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
}
const SNAP_GRID = 10;

const SvgCanvasDesigner: React.FC = () => {
  const canvas = useSvgStore((state) => state.canvas);

  const selectedState = useSvgStore((state) => state.selectedState);
  const setSelectedIds = useSvgStore((state) => state.setSelectedIds);
  const setActiveElement = useSvgStore((state) => state.setActiveElement);
  const updateElements = useSvgStore((state) => state.updateElements);
  const removeElements = useSvgStore((state) => state.removeElements);

  const isSubElementEditMode = useSvgStore((state) => state.isSubElementEditMode);
  const editingNodeId = useSvgStore((state) => state.editingNodeId);
  const selectedSubElementId = useSvgStore((state) => state.selectedSubElementId);
  const enterSubElementEditMode = useSvgStore((state) => state.enterSubElementEditMode);
  const exitSubElementEditMode = useSvgStore((state) => state.exitSubElementEditMode);
  const selectSubElement = useSvgStore((state) => state.selectSubElement);
  const parseNodeSubElements = useSvgStore((state) => state.parseNodeSubElements);

  const [interactionMode, setInteractionMode] = useState<'IDLE' | 'SELECTING' | 'DRAGGING' | 'PANNING'>('IDLE');
  const [selectionBox, setSelectionBox] = useState<SelectionBox | null>(null);
  const [selectedBBoxes, setSelectedBBoxes] = useState<Record<string, DOMRect>>({});

  const dragStartRef = useRef<{ x: number; y: number } | null>(null);
  const initialPositionsRef = useRef<Map<string, { x: number; y: number }>>(new Map());
  const svgRef = useRef<SVGSVGElement>(null);
  
  // 记录拖拽开始时的 BBox，拖拽过程中不重新计算
  const dragStartBBoxesRef = useRef<Record<string, DOMRect>>({});

  /**
   * 计算选中元素的 BBox
   * 只在选中状态变化且不在拖拽时调用
   */
  const updateBBoxes = useCallback(() => {
    const newBBoxes: Record<string, DOMRect> = {};
    if (selectedState.selectedIds.length > 0) {
      selectedState.selectedIds.forEach(id => {
        const el = document.getElementById(`node-content-${id}`) as SVGGraphicsElement | null;
        if (el && typeof el.getBBox === 'function') {
          try {
            const bbox = el.getBBox();
            // 设置最小尺寸
            const adjustedBBox = new DOMRect(
              bbox.x,
              bbox.y,
              bbox.width === 0 ? 50 : bbox.width,
              bbox.height === 0 ? 50 : bbox.height
            );
            newBBoxes[id] = adjustedBBox;
          } catch (e) {
            console.warn('Cannot retrieve BBox for', id);
          }
        }
      });
    }
    setSelectedBBoxes(newBBoxes);
  }, [selectedState.selectedIds]);

  // 只在选中 ID 变化时更新 BBox，不依赖 elements
  useLayoutEffect(() => {
    // 如果正在拖拽，不更新 BBox
    if (interactionMode === 'DRAGGING') {
      return;
    }
    
    // 延迟一帧确保 DOM 已更新
    requestAnimationFrame(() => {
      updateBBoxes();
    });
  }, [selectedState.selectedIds, interactionMode, updateBBoxes]);

  const handleDeleteSelected = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (selectedState.selectedIds.length > 0) {
      if (removeElements) {
        removeElements(selectedState.selectedIds);
      }
      setSelectedIds([]);
      setActiveElement(undefined);
    }
  };

  const getMousePosition = (evt: React.MouseEvent | MouseEvent) => {
    if (!svgRef.current) return { x: 0, y: 0 };
    const CTM = svgRef.current.getScreenCTM();
    if (!CTM) return { x: 0, y: 0 };
    return {
      x: (evt.clientX - CTM.e) / CTM.a,
      y: (evt.clientY - CTM.f) / CTM.d
    };
  };

  const handleElementDoubleClick = (e: React.MouseEvent, elementId: string) => {
    e.stopPropagation();
    parseNodeSubElements(elementId);
    enterSubElementEditMode(elementId);
  };

  /**
   * 处理子元素点击 - 从事件目标向上查找 data-sub-id
   */
  const handleSubElementClick = (e: React.MouseEvent, nodeId: string) => {
    if (!isSubElementEditMode || editingNodeId !== nodeId) {
      return;
    }

    e.stopPropagation();

    // 从点击目标向上查找带有 data-sub-id 的元素
    let target = e.target as Element | null;
    while (target && target !== e.currentTarget) {
      const subId = target.getAttribute('data-sub-id');
      if (subId) {
        selectSubElement(subId);
        return;
      }
      target = target.parentElement;
    }

    // 点击了内容区域但没有命中任何子元素，取消选中
    selectSubElement(null);
  };

  const handleStageMouseDown = (e: React.MouseEvent) => {
    const target = e.target as Element;
    if (target.id === 'stage-bg') {
      if (isSubElementEditMode) {
        exitSubElementEditMode();
      }

      const pos = getMousePosition(e);
      setInteractionMode('SELECTING');
      setSelectionBox({
        startX: pos.x,
        startY: pos.y,
        currentX: pos.x,
        currentY: pos.y,
      });
      if (!e.shiftKey) {
        setSelectedIds([]);
        setActiveElement(undefined);
      }
    }
  };

  const handleElementMouseDown = (e: React.MouseEvent, elementId: string) => {
    // 在子元素编辑模式下，不触发整体拖拽
    if (isSubElementEditMode && editingNodeId === elementId) {
      return;
    }

    e.stopPropagation();
    const pos = getMousePosition(e);
    let newSelectedIds = [...selectedState.selectedIds];

    if (!e.shiftKey) {
      if (!newSelectedIds.includes(elementId) || newSelectedIds.length > 1) {
        setActiveElement(elementId);
        newSelectedIds = [elementId];
        setSelectedIds(newSelectedIds);
      } else {
        setActiveElement(elementId);
      }
    } else {
      if (newSelectedIds.includes(elementId)) {
        newSelectedIds = newSelectedIds.filter(id => id !== elementId);
      } else {
        newSelectedIds.push(elementId);
      }
      setSelectedIds(newSelectedIds);
    }

    // 保存拖拽开始时的 BBox
    dragStartBBoxesRef.current = { ...selectedBBoxes };

    setInteractionMode('DRAGGING');
    dragStartRef.current = pos;

    const initialMap = new Map();
    if (canvas) {
      canvas.elements.forEach(el => {
        if (newSelectedIds.includes(el.id) && el.type === 'svg') {
          initialMap.set(el.id, { x: (el as SvgNodeElement).x, y: (el as SvgNodeElement).y });
        }
      });
    }
    initialPositionsRef.current = initialMap;
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    const pos = getMousePosition(e);

    if (interactionMode === 'SELECTING' && selectionBox) {
      setSelectionBox({
        ...selectionBox,
        currentX: pos.x,
        currentY: pos.y,
      });
    } else if (interactionMode === 'DRAGGING' && dragStartRef.current && canvas) {
      if (initialPositionsRef.current.size === 0) return;

      const dx = pos.x - dragStartRef.current.x;
      const dy = pos.y - dragStartRef.current.y;

      const updates = [];
      for (const [id, initPos] of initialPositionsRef.current.entries()) {
        let nextX = initPos.x + dx;
        let nextY = initPos.y + dy;
        nextX = Math.round(nextX / SNAP_GRID) * SNAP_GRID;
        nextY = Math.round(nextY / SNAP_GRID) * SNAP_GRID;
        updates.push({ id, partial: { x: nextX, y: nextY } });
      }
      updateElements(updates);
    }
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    if (interactionMode === 'SELECTING' && selectionBox && canvas) {
      const x1 = Math.min(selectionBox.startX, selectionBox.currentX);
      const x2 = Math.max(selectionBox.startX, selectionBox.currentX);
      const y1 = Math.min(selectionBox.startY, selectionBox.currentY);
      const y2 = Math.max(selectionBox.startY, selectionBox.currentY);

      const MIN_SELECT_SIZE = 4;
      if (Math.abs(x2 - x1) < MIN_SELECT_SIZE && Math.abs(y2 - y1) < MIN_SELECT_SIZE) {
        setSelectedIds([]);
        setActiveElement(undefined);
      } else {
        const newlySelected: string[] = [];

        canvas.elements.forEach((el) => {
          if (el.type !== 'svg') return;
          const node = el as SvgNodeElement;

          let elRect = { x: node.x, y: node.y, width: 50, height: 50 };

          const domEl = document.getElementById(`node-content-${node.id}`) as SVGGraphicsElement | null;
          if (domEl && typeof domEl.getBBox === 'function') {
            try {
              const bbox = domEl.getBBox();
              const scale = node.scale ?? 1;

              elRect = {
                x: node.x + bbox.x * scale,
                y: node.y + bbox.y * scale,
                width: bbox.width * scale,
                height: bbox.height * scale,
              };
            } catch {
              // 失败就用默认 elRect
            }
          }

          const isIntersecting = !(
            x1 > elRect.x + elRect.width ||
            x2 < elRect.x ||
            y1 > elRect.y + elRect.height ||
            y2 < elRect.y
          );

          if (isIntersecting) {
            newlySelected.push(node.id);
          }
        });

        let finalSelection: string[];

        if (e.shiftKey) {
          const merged = new Set<string>([...selectedState.selectedIds, ...newlySelected]);
          finalSelection = Array.from(merged);
        } else {
          finalSelection = newlySelected;
        }

        setSelectedIds(finalSelection);

        if (finalSelection.length === 1) {
          setActiveElement(finalSelection[0]);
        } else {
          setActiveElement(undefined);
        }
      }
    }
    
    // 拖拽结束后，更新 BBox
    if (interactionMode === 'DRAGGING') {
      // 延迟更新 BBox，确保 DOM 已更新
      requestAnimationFrame(() => {
        updateBBoxes();
      });
    }

    setInteractionMode('IDLE');
    setSelectionBox(null);
    dragStartRef.current = null;
    initialPositionsRef.current.clear();
    dragStartBBoxesRef.current = {};
  };

  useEffect(() => {
    (window as any).__AGI_SVG_REF__ = svgRef.current;
  });

  /**
   * 获取渲染用的 SVG 内容
   * - 应用子元素自定义属性
   * - 注入动画标签（仅当不是原始动画时）
   * - 编辑模式下添加 data-sub-id
   */
  const getNodeSvgContent = (node: SvgNodeElement): string => {
    let content = node.svgContent;

    // 1. 如果有子元素自定义属性，先应用它们
    if (node.subElements && node.subElements.length > 0) {
      // 检查是否有任何自定义属性被设置
      const hasCustomAttributes = (elements: typeof node.subElements): boolean => {
        for (const el of elements) {
          if (Object.keys(el.customAttributes).length > 0) return true;
          if (el.children && hasCustomAttributes(el.children)) return true;
        }
        return false;
      };
      
      if (hasCustomAttributes(node.subElements)) {
        content = rebuildSvgContent(content, node.subElements);
      }
    }

    // 2. 注入动画（仅当动画类型不是 none 且不是原始动画时）
    // 如果是原始动画（isOriginalAnimation = true），SVG 中已经有动画标签，不需要重新注入
    if (
      node.animation &&
      node.animation.animationType &&
      node.animation.animationType !== 'none' &&
      !node.animation.isOriginalAnimation // 关键：原始动画不重新注入
    ) {
      content = injectAnimationToSvg(content, node.animation);
    }

    // 3. 编辑模式下，添加 data-sub-id 和高亮
    if (isSubElementEditMode && editingNodeId === node.id && node.subElements) {
      content = prepareSvgForEditMode(content, node.subElements, selectedSubElementId);
    }

    return content;
  };

  if (!canvas) {
    return (
      <div className="ai-svg-panel ai-svg-panel-center">
        <div className="ai-svg-preview-placeholder">正在初始化画布...</div>
      </div>
    );
  }

  return (
    <div className="ai-svg-panel ai-svg-panel-center">
      <div className="ai-svg-panel-header">
        <span className="ai-svg-panel-title">画布设计器</span>
        {isSubElementEditMode && (
          <span style={{ marginLeft: '8px', fontSize: '12px', color: '#3b82f6', background: '#eff6ff', padding: '2px 8px', borderRadius: '4px' }}>
            子元素编辑模式
          </span>
        )}
      </div>
      <div className="ai-svg-canvas-wrapper" style={{ cursor: interactionMode === 'PANNING' ? 'grab' : 'default' }}>
        <svg
          ref={svgRef}
          className="ai-svg-canvas"
          width={canvas.width}
          height={canvas.height}
          viewBox={`0 0 ${canvas.width} ${canvas.height}`}
          onMouseDown={handleStageMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >

          <rect id="stage-bg" width="100%" height="100%" fill="transparent" />

          {canvas.elements.map((element) => {
            if (element.type === 'svg') {
              const node = element;
              const hit = selectedState.selectedIds.includes(node.id);
              const isEditing = isSubElementEditMode && editingNodeId === node.id;
              
              // 拖拽时使用保存的 BBox，否则使用当前 BBox
              const bbox = interactionMode === 'DRAGGING' 
                ? dragStartBBoxesRef.current[node.id] 
                : selectedBBoxes[node.id];

              const x = node.x ?? 0;
              const y = node.y ?? 0;
              const scale = node.scale ?? 1;
              const rotation = node.rotation ?? 0;

              const transform = `translate(${x}, ${y}) scale(${scale}) rotate(${rotation})`;

              // 获取渲染内容（包含动画和编辑模式处理）
              const svgContent = getNodeSvgContent(node);

              return (
                <g
                  key={node.id}
                  id={`node-group-${node.id}`}
                  transform={transform}
                  opacity={node.opacity}
                  onMouseDown={(e) => handleElementMouseDown(e, node.id)}
                  onDoubleClick={(e) => handleElementDoubleClick(e, node.id)}
                  style={{ cursor: isEditing ? 'default' : 'move' }}
                >
                  {/* SVG 内容 - 在编辑模式下监听点击 */}
                  {/* 添加 CSS 确保动画元素不阻挡点击 */}
                  <g
                    id={`node-content-${node.id}`}
                    dangerouslySetInnerHTML={{ __html: svgContent }}
                    onClick={isEditing ? (e) => handleSubElementClick(e, node.id) : undefined}
                    style={isEditing ? { cursor: 'pointer' } : undefined}
                    className="svg-node-content"
                  />

                  {/* 选中时的包围盒与操作 UI（非编辑模式） */}
                  {hit && bbox && !isEditing && (
                    <g pointerEvents="none">
                      <rect
                        x={bbox.x - 2}
                        y={bbox.y - 2}
                        width={bbox.width + 4}
                        height={bbox.height + 4}
                        fill="none"
                        stroke="#3b82f6"
                        strokeWidth="1.5"
                        strokeDasharray="4 2"
                      />
                      <text x={bbox.x} y={bbox.y - 6} fontSize="10" fill="#3b82f6">
                        {Math.round(bbox.width * scale)} x {Math.round(bbox.height * scale)}
                      </text>

                      <g
                        transform={`translate(${bbox.x + bbox.width + 10}, ${bbox.y})`}
                        style={{ cursor: 'pointer', pointerEvents: 'auto' }}
                        onClick={handleDeleteSelected}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <circle r="8" fill="#ef4444" />
                        <text y="3" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">×</text>
                      </g>
                    </g>
                  )}

                  {/* 编辑模式下的特殊边框 */}
                  {isEditing && bbox && (
                    <g pointerEvents="none">
                      <rect
                        x={bbox.x - 4}
                        y={bbox.y - 4}
                        width={bbox.width + 8}
                        height={bbox.height + 8}
                        fill="none"
                        stroke="#10b981"
                        strokeWidth="2"
                        strokeDasharray="6 3"
                      />
                      <text x={bbox.x} y={bbox.y - 8} fontSize="10" fill="#10b981">
                        编辑中 - 点击选择子元素
                      </text>
                    </g>
                  )}
                </g>
              );
            }
            return null;
          })}

          {/* 框选矩形 */}
          {selectionBox && (
            <rect
              x={Math.min(selectionBox.startX, selectionBox.currentX)}
              y={Math.min(selectionBox.startY, selectionBox.currentY)}
              width={Math.abs(selectionBox.currentX - selectionBox.startX)}
              height={Math.abs(selectionBox.currentY - selectionBox.startY)}
              fill="rgba(59, 130, 246, 0.1)"
              stroke="#3b82f6"
              strokeWidth="1"
              pointerEvents="none"
            />
          )}
        </svg>
      </div>
    </div>
  );
};

export default SvgCanvasDesigner;